import type { ReactNode } from 'react';
import { SignedIn, UserButton } from '@clerk/clerk-react';
import { Link, useRouter, match } from '../router';
import { Avatar } from './Avatar';
import { UiNode } from '../lib/UiNode';
import { Logo } from '../App';
import { NotificationsBell } from './Notifications';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { PushPrompt } from './PushPrompt';
import { UpdateAvailableBanner } from './UpdateAvailableBanner';
import { IconCalendar } from './Icon';
import { QuickAddTaskButton } from './QuickAddTask';
import type { User } from '../../shared/types';

export function Layout({
  user,
  tenant,
  workspaces,
  children,
}: {
  user: User;
  tenant: { id: string; slug: string; name: string } | null;
  workspaces: Array<{
    id: string;
    slug: string;
    name: string;
    isPersonal: boolean;
    role: 'admin' | 'member';
  }>;
  children: ReactNode;
}) {
  const { pathname } = useRouter();
  const onBoard = match('/boards/:id', pathname);

  return (
    <div className="flex h-full flex-col">
      <header className="sticky top-0 z-30 border-b border-ink-rim bg-ink/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <UiNode
              as={Link}
              name="app-home-link"
              entity="app"
              action="navigate_home"
              to="/"
              className="flex items-center gap-2.5 text-cream"
            >
              <Logo size={22} />
              <span className="text-[15px] font-semibold tracking-tight">SynFolio</span>
            </UiNode>
            <WorkspaceSwitcher
              current={tenant}
              workspaces={workspaces}
              onChange={() => {
                /* navigation handled inline */
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <QuickAddTaskButton />
            <Link
              to="/calendar"
              className="btn btn-ghost inline-flex items-center gap-1.5 !px-2.5 !py-1.5 text-xs"
            >
              <IconCalendar size={14} />
              <span className="hidden sm:inline">Calendar</span>
            </Link>
            <NotificationsBell />
            {!onBoard && (
              <span className="hidden text-xs text-cream-3 sm:inline">{user.name}</span>
            )}
            {/* Clerk's UserButton hosts the sign-out + account menu.
                Only render the SignedIn gate when ClerkProvider is
                actually mounted — Clerk now throws when its components
                appear outside the provider. */}
            {import.meta.env.VITE_CLERK_PUBLISHABLE_KEY && (
              <SignedIn>
                <UserButton
                  appearance={{
                    elements: { avatarBox: 'h-[30px] w-[30px]' },
                  }}
                />
              </SignedIn>
            )}
            {!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY && (
              <UiNode
                as={Link}
                name="app-profile-link"
                entity="user"
                action="open_profile"
                to="/profile"
                className="inline-flex"
              >
                <Avatar user={user} size={30} />
              </UiNode>
            )}
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-5 sm:py-8">
        <PushPrompt />
        {children}
      </main>
      <UpdateAvailableBanner />
    </div>
  );
}
